import React from 'react';
import { useField } from 'react-final-form';
import { Autocomplete, TextField } from 'housecall-ui';

import { getErrorMessage, hasError } from './utils';

type Props<T> = Omit<
  React.ComponentProps<typeof Autocomplete>,
  'value' | 'onChange' | 'onBlur' | 'onFocus' | 'error' | 'renderInput'
> &
  Pick<React.ComponentProps<typeof TextField>, 'label' | 'helperText'> & {
    name: T;
    InputProps?: Omit<React.ComponentProps<typeof TextField>, 'label' | 'helperText'>;
  };

export const AutocompleteField = <T extends string>({
  name,
  label,
  helperText,
  InputProps,
  ...restProps
}: Props<T>) => {
  const { input, meta } = useField(name);

  return (
    <Autocomplete
      {...input}
      // Suppress MUI's Autocomplete warning, by fallback to null
      // @see https://github.com/mui/material-ui/blob/v4.9.14/packages/material-ui-lab/src/useAutocomplete/useAutocomplete.js#L205-L223
      value={input.value || null}
      onChange={(_, value) => input.onChange(value)}
      renderInput={(params) => (
        // data-test-id provided via restTextFieldProps
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        <TextField
          label={label}
          helperText={getErrorMessage(meta, helperText)}
          error={hasError(meta)}
          {...InputProps}
          {...params}
        />
      )}
      {...restProps}
    />
  );
};
